// Helpers PUROS de formato para el bloque de KPIs comparativos del dashboard de
// Service Validation. Consumen el shape `KpiMetric` del contrato del backend:
//   { current, previous, delta, delta_pct }  (todos float; delta_pct float|null)
//
// Reglas del contrato que estos helpers encapsulan:
//  - `delta_pct === null` => NO hay base de comparación (previous == 0): se
//    renderiza "—" / etiqueta "nuevo", nunca 0%.
//  - `validated_auto_pct` viene en PUNTOS porcentuales (0-100) y su delta también
//    está en puntos %: se formatea con `formatPercentPoints` / sufijo "pp".
//  - `amount_total` es monto (MXN): `formatCurrency`.
//  - `avg_validation_seconds` es duración en segundos: `formatDuration`.
//
// Son funciones puras y testeables (patrón de auditFormat.js / filterDefaults.js).

// Formatea un número legible (enteros sin decimales; decimales con hasta 2).
// `null`/`undefined`/NaN => "—".
export const formatNumber = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return "—"
  const n = Number(v)
  return Number.isInteger(n)
    ? n.toLocaleString("es-MX")
    : n.toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
}

// Formatea un monto como moneda MXN. `null`/NaN => "—".
export const formatCurrency = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return "—"
  return Number(v).toLocaleString("es-MX", {
    style: "currency",
    currency: "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

// Formatea una tasa en PUNTOS porcentuales (0-100). Un decimal + sufijo "%".
// `null`/NaN => "—".
export const formatPercentPoints = (v) => {
  if (v == null || !Number.isFinite(Number(v))) return "—"
  return `${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`
}

// Formatea segundos a una duración legible (h/m/s). "—" si no hay dato.
// Reutiliza la lógica de `fmtDuration` ya presente en el componente para que el
// formato sea idéntico.
export const formatDuration = (secs) => {
  if (secs == null || !Number.isFinite(Number(secs))) return "—"
  const s = Math.round(Number(secs))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${sec}s` : `${sec}s`
}

// Formatea el delta ABSOLUTO de un KpiMetric con signo explícito (+/-).
// `fmt` es la función de formato del valor (por defecto `formatNumber`), para
// reutilizarlo con monto/puntos %/duración. `null`/sin métrica => "—".
export const formatDelta = (metric, fmt = formatNumber) => {
  if (!metric || metric.delta == null || !Number.isFinite(Number(metric.delta))) return "—"
  const d = Number(metric.delta)
  const body = fmt(Math.abs(d))
  if (d > 0) return `+${body}`
  if (d < 0) return `-${body}`
  return body
}

// Formatea el delta porcentual (`delta_pct`) de un KpiMetric. `null` => `null`
// (el caller renderiza "—" / etiqueta "nuevo"). Si hay valor: signo + un decimal + "%".
export const formatDeltaPct = (metric) => {
  if (!metric || metric.delta_pct == null || !Number.isFinite(Number(metric.delta_pct))) return null
  const p = Number(metric.delta_pct)
  const sign = p > 0 ? "+" : p < 0 ? "-" : ""
  const body = Math.abs(p).toLocaleString("es-MX", { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  return `${sign}${body}%`
}

// Dirección del delta para color/icono: "up" | "down" | "flat".
// Sin métrica o delta no finito => "flat".
export const deltaDirection = (metric) => {
  if (!metric || metric.delta == null || !Number.isFinite(Number(metric.delta))) return "flat"
  const d = Number(metric.delta)
  if (d > 0) return "up"
  if (d < 0) return "down"
  return "flat"
}
